import React, { useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Alert,
  Button,
  Tabs,
  Tab,
  TablePagination,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  GetApp as ExportIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from 'recharts';

import { ReportResult } from '../../services/reportingService';

interface ReportViewerProps {
  reportResult: (ReportResult & { error?: string }) | null;
  onRefresh?: () => void;
  onExport?: (format: string) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`report-tabpanel-${index}`}
      aria-labelledby={`report-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

const ReportViewer: React.FC<ReportViewerProps> = ({ reportResult, onRefresh, onExport }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Early return if no data
  if (!reportResult) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" color="text.secondary">
            No report data available
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Process data for visualizations
  const chartData = useMemo(() => {
    if (!reportResult?.data?.length) return [];
    
    const { grouped, aggregations } = reportResult;
    
    if (grouped) {
      return Object.entries(grouped).map(([key, value]) => {
        const groupValue = value as Record<string, unknown>;
        return {
          name: key,
          count: Array.isArray(groupValue) ? groupValue.length : (groupValue.count as number) || 0,
          ...(groupValue.aggregations as Record<string, unknown>) || {}
        };
      });
    }
    
    if (aggregations) {
      return Object.entries(aggregations).map(([key, value]) => ({
        name: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        value: value
      }));
    }
    
    return [];
  }, [reportResult]);

  // Get table columns dynamically
  const tableColumns = useMemo(() => {
    if (!reportResult?.data?.length) return [];
    
    const firstItem = reportResult.data[0];
    if (!firstItem || typeof firstItem !== 'object') return [];
    
    return Object.keys(firstItem).map(key => ({
      id: key,
      label: key.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()),
      format: (value: unknown) => {
        if (value === null || value === undefined) return '-';
        if (typeof value === 'object') return JSON.stringify(value, null, 1);
        if (typeof value === 'boolean') return value ? 'Yes' : 'No';
        if (key.includes('At') || key.includes('Date')) {
          try {
            return new Date(value as string | number | Date).toLocaleString();
          } catch {
            return String(value);
          }
        }
        return value;
      }
    }));
  }, [reportResult]);

  // Paginated data
  const paginatedData = useMemo(() => {
    if (!reportResult?.data) return [];
    return reportResult.data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [reportResult?.data, page, rowsPerPage]);

  if (!reportResult) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" color="text.secondary" align="center">
            Run a query to see results here
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (reportResult.error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">
            <Typography variant="h6">Error</Typography>
            <Typography>{reportResult.error}</Typography>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6">
            Report Results ({reportResult.totalCount} total records)
          </Typography>
          <Box>
            {onRefresh && (
              <Button
                startIcon={<RefreshIcon />}
                onClick={onRefresh}
                variant="outlined"
                sx={{ mr: 1 }}
              >
                Refresh
              </Button>
            )}
            {onExport && (
              <Button
                startIcon={<ExportIcon />}
                onClick={() => onExport('csv')}
                variant="contained"
              >
                Export
              </Button>
            )}
          </Box>
        </Box>

        {/* Summary Stats */}
        {reportResult.aggregations && Object.keys(reportResult.aggregations).length > 0 && (
          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">Summary Statistics</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {Object.entries(reportResult.aggregations).map(([key, value]) => (
                  <Grid size={{ xs: 12, sm: 6, md: 3 }} key={key}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography color="text.secondary" gutterBottom>
                          {key.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                        </Typography>
                        <Typography variant="h5">
                          {typeof value === 'number' ? 
                            (key.includes('avg') || key.includes('rate') ? 
                              (value as number).toFixed(2) : 
                              (value as number).toLocaleString()
                            ) : 
                            String(value)
                          }
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="Table View" />
            {chartData.length > 0 && <Tab label="Chart View" />}
            {reportResult.grouped && <Tab label="Grouped Data" />}
          </Tabs>
        </Box>

        {/* Table View */}
        <TabPanel value={activeTab} index={0}>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  {tableColumns.map((column) => (
                    <TableCell key={column.id}>
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((row, index: number) => {
                  const typedRow = row as Record<string, unknown>;
                  return (
                    <TableRow key={index}>
                      {tableColumns.map((column) => (
                        <TableCell key={column.id}>
                          {String(column.format(typedRow[column.id]))}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={reportResult.totalCount || 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        </TabPanel>

        {/* Chart View */}
        {chartData.length > 0 && (
          <TabPanel value={activeTab} index={1}>
            <Grid container spacing={3}>
              {/* Bar Chart */}
              {chartData.length > 0 && (
                <Grid size={{ xs: 12, lg: 6 }}>
                  <Typography variant="h6" gutterBottom>
                    Distribution
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill="#8884d8" />
                    </BarChart>
                  </ResponsiveContainer>
                </Grid>
              )}

              {/* Pie Chart */}
              {chartData.length > 0 && chartData.length < 10 && (
                <Grid size={{ xs: 12, lg: 6 }}>
                  <Typography variant="h6" gutterBottom>
                    Breakdown
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={chartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, value }) => `${name}: ${value}`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="count"
                      >
                        {chartData.map((_entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Grid>
              )}

              {/* Time Series Line Chart (if date field present) */}
              {chartData.some(item => item.name && !isNaN(Date.parse(item.name))) && (
                <Grid size={12}>
                  <Typography variant="h6" gutterBottom>
                    Trend Over Time
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="count" stroke="#8884d8" />
                    </LineChart>
                  </ResponsiveContainer>
                </Grid>
              )}
            </Grid>
          </TabPanel>
        )}

        {/* Grouped Data View */}
        {reportResult.grouped && (
          <TabPanel value={activeTab} index={chartData.length > 0 ? 2 : 1}>
            <Box>
              {Object.entries(reportResult.grouped).map(([groupKey, groupData]) => {
                const typedGroupData = groupData as Record<string, unknown>;
                return (
                  <Accordion key={groupKey} sx={{ mb: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Typography variant="subtitle1">{groupKey}</Typography>
                        <Chip 
                          label={Array.isArray(typedGroupData) ? `${typedGroupData.length} items` : `${(typedGroupData.count as number) || 0} items`}
                          size="small"
                          color="primary"
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {Array.isArray(typedGroupData) ? (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              {tableColumns.map((column) => (
                                <TableCell key={column.id}>
                                  {column.label}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {typedGroupData.slice(0, 10).map((row, index) => {
                              const typedRow = row as Record<string, unknown>;
                              return (
                                <TableRow key={index}>
                                  {tableColumns.map((column) => (
                                    <TableCell key={column.id}>
                                      {String(column.format(typedRow[column.id]))}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                        {typedGroupData.length > 10 && (
                          <Box p={2} textAlign="center">
                            <Typography variant="body2" color="text.secondary">
                              Showing first 10 of {typedGroupData.length} items
                            </Typography>
                          </Box>
                        )}
                      </TableContainer>
                    ) : (
                      <Grid container spacing={2}>
                        <Grid size={12}>
                          <Typography variant="body2">
                            Items: {String((typedGroupData.count as number) || 0)}
                          </Typography>
                        </Grid>
                        {(() => {
                          const aggregations = typedGroupData.aggregations;
                          if (aggregations && typeof aggregations === 'object') {
                            return (
                              <Grid size={12}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Aggregations:
                                </Typography>
                                <Grid container spacing={1}>
                                  {Object.entries(aggregations as Record<string, unknown>).map(([key, value]) => (
                                    <Grid key={key}>
                                      <Chip 
                                        label={`${key}: ${String(value)}`} 
                                        size="small" 
                                        variant="outlined"
                                      />
                                    </Grid>
                                  ))}
                                </Grid>
                              </Grid>
                            );
                          }
                          return null;
                        })()}
                      </Grid>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })}
            </Box>
          </TabPanel>
        )}
      </CardContent>
    </Card>
  );
};

export default ReportViewer;